import {getOwnedNodes, TemplateNodes, TemplateGraph, deserializeTemplateGraph} from "@cm/template-nodes"
import {Component, computed, inject, output, ChangeDetectionStrategy} from "@angular/core"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {cardAnimations} from "@app/common/components/cards/animations"
import {DialogComponent} from "@app/common/components/dialogs/dialog/dialog.component"
import {BaseSelectComponent} from "@app/platform/components/base/base-select/base-select.component"
import {DIALOG_DEFAULT_WIDTH} from "@app/template-editor/helpers/constants"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {TemplateFilterInput, TemplateType} from "@generated"
import {TemplateAddTemplateItemFragment, TemplateAddTemplateItemsGQL} from "@template-editor/components/template-add-product/template-add-product.generated"
import {GetTemplateRevisionDetailsTemplateAddSceneGQL} from "@template-editor/components/template-add-scene/template-add-scene.generated"
import {firstValueFrom} from "rxjs"
import {CardErrorComponent} from "@app/common/components/cards/card-error/card-error.component"
import {CardPlaceholderComponent} from "@app/common/components/cards/card-placeholder/card-placeholder.component"
import {OwnOrganizationsFilterComponent} from "@app/common/components/filters/own-organizations-filter/own-organizations-filter.component"
import {SearchComponent} from "@app/common/components/inputs/search/search.component"
import {InfiniteListComponent} from "@app/common/components/lists/infinite-list/infinite-list.component"
import {TemplateAddCardComponent} from "@app/template-editor/components/template-add-card/template-add-card.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-add-scene",
    templateUrl: "./template-add-scene.component.html",
    styleUrl: "./template-add-scene.component.scss",
    animations: [...cardAnimations],
    imports: [SearchComponent, InfiniteListComponent, CardErrorComponent, TemplateAddCardComponent, CardPlaceholderComponent, OwnOrganizationsFilterComponent],
})
export class TemplateAddSceneComponent extends BaseSelectComponent<TemplateAddTemplateItemFragment, TemplateFilterInput> {
    readonly templatesGql = inject(TemplateAddTemplateItemsGQL)
    readonly templateRevisionGql = inject(GetTemplateRevisionDetailsTemplateAddSceneGQL)
    private rawData = this.implementDataLoader(fetchThrowingErrors(this.templatesGql))
    readonly $data = computed(() => this.rawData().items.map((item) => ({...item, action: item.data ? () => this.addScene(item.data) : undefined})))
    readonly onItemClicked = output()

    private readonly sceneManagerService = inject(SceneManagerService)
    private readonly dialog = inject(MatDialog)

    constructor() {
        super()
        this.$customFilters.set({type: [TemplateType.Scene]})
    }

    private confirmSave(title: string, message: string) {
        const dialogRef: MatDialogRef<DialogComponent, boolean> = this.dialog.open(DialogComponent, {
            disableClose: false,
            width: DIALOG_DEFAULT_WIDTH,
            data: {
                title: title,
                message: message,
                confirmLabel: "Yes",
                cancelLabel: "No",
            },
        })

        return dialogRef.afterClosed()
    }

    private async addScene(data: TemplateAddTemplateItemFragment): Promise<false | void> {
        const legacyId = data.latestRevision?.legacyId
        if (legacyId === undefined) return

        const templateGraph = this.sceneManagerService.$templateGraph()
        if (getOwnedNodes(templateGraph).length > 0) {
            const saveConfirmed = await firstValueFrom(
                this.confirmSave(
                    "Replace template with environment?",
                    `The current template will be completely replaced with the selected environment </br></br><b>${data.name}</b></br></br>Proceed?`,
                ),
            )
            if (!saveConfirmed) return false
        }

        const action = async () => {
            const templateRevision = await fetchThrowingErrors(this.templateRevisionGql)({legacyId})
            const graphJson = templateRevision.templateRevision.graph

            const newGraph = !graphJson
                ? new TemplateGraph({parameters: {name: "Untitled Template", nodes: new TemplateNodes({parameters: {list: []}})}})
                : deserializeTemplateGraph(graphJson)

            this.sceneManagerService.modifyTemplateGraph((templateGraph) => {
                templateGraph.parameters.nodes.clear()
                templateGraph.updateParameters({nodes: newGraph.parameters.nodes.clone({cloneSubNode: () => true})})
            })
        }

        action()
    }
}
